import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useNavigate } from "react-router-dom";
import SpinnerOverlay from "../SpinnerOverlay";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

const sliderImagesDesktop = [
  '/images/site-banners/desktop-1.jpg',
  '/images/site-banners/desktop-2-2.jpg',
  '/images/site-banners/desktop-3.jpg',
];
const sliderImagesMobile = [
  '/images/site-banners/mobileee-1.jpg',
  '/images/site-banners/mobileee-2-2.jpg',
  '/images/site-banners/mobileee-3.jpg',
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

/**
 * HeroSwiper component for displaying hero banners.
 * 
 * @returns {JSX.Element} HeroSwiper component.
 */
const HeroSwiper = () => {
  const isMobile = useIsMobile();
  const images = isMobile ? sliderImagesMobile : sliderImagesDesktop;
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Handles animated navigation
  const handleSlideClick = (to) => {
    setIsTransitioning(true);
    setTimeout(() => {
      navigate(to);
      setIsTransitioning(false);
    }, 250);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Overlay for transition */}
      <SpinnerOverlay show={isTransitioning} />
      
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        navigation
        pagination={{ clickable: true }}
        effect="fade"
        loop
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        className="hero-swiper"
        style={{ width: "100%", height: "100%" }}
      >
        {images.map((src, idx) => (
          <SwiperSlide key={idx} style={{ width: "100%" }}>
            {idx === 0 ? (
              <div
                onClick={() => !isTransitioning && handleSlideClick('/categories/leather-double-tool-pouch')}
                style={{ cursor: 'pointer', display: 'block', width: '100%', height: '100%' }}
                tabIndex={0}
                role="button"
                aria-label="Go to Leather Double Tool Pouch"
                onKeyPress={e => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isTransitioning) handleSlideClick('/categories/leather-double-tool-pouch');
                }}
              >
                <div
                  style={{
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "#fff"
                  }}
                >
                  <img
                    src={src}
                    alt={"Hero Banner"}
                    style={{
                      display: "block",
                      maxWidth: "100vw",
                      maxHeight: "100vh",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.06)"
                    }}
                  />
                </div>
              </div>
            ) : idx === 1 ? (
              <div
                onClick={() => !isTransitioning && handleSlideClick('/contact')}
                style={{ cursor: 'pointer', display: 'block', width: '100%', height: '100%' }}
                tabIndex={0}
                role="button"
                aria-label="Go to Contact"
                onKeyPress={e => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isTransitioning) handleSlideClick('/contact');
                }}
              >
                <div
                  style={{
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "#fff"
                  }}
                >
                  <img
                    src={src}
                    alt={"Hero Banner"}
                    style={{
                      display: "block",
                      maxWidth: "100vw",
                      maxHeight: "100vh",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.06)"
                    }}
                  />
                </div>
              </div>
            ) : idx === 2 ? (
              <div
                onClick={() => !isTransitioning && handleSlideClick('/categories/polyester-double-tool-pouch')}
                style={{ cursor: 'pointer', display: 'block', width: '100%', height: '100%' }}
                tabIndex={0}
                role="button"
                aria-label="Go to Polyester Double Tool Pouch"
                onKeyPress={e => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isTransitioning) handleSlideClick('/categories/polyester-double-tool-pouch');
                }}
              >
                <div
                  style={{
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "#fff"
                  }}
                >
                  <img
                    src={src}
                    alt={"Hero Banner"}
                    style={{
                      display: "block",
                      maxWidth: "100vw",
                      maxHeight: "100vh",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.06)"
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  background: "#fff"
                }}
              >
                <img
                  src={src}
                  alt={"Hero Banner"}
                  style={{
                    display: "block",
                    maxWidth: "100vw",
                    maxHeight: "100vh",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)"
                  }}
                />
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default HeroSwiper;
